import type { DriveStep } from 'driver.js';
import type React from 'react';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { driver } from './driver';
import { PopoverPortal } from './PopoverPortal';
import { getStepElement, getStepElementSelector } from './stage';
import type { Listener, ListernerElement, TourContextData, TourOptions, TourStep } from './types';

export const TourContext = createContext<TourContextData>({
  initDriver: () => {},
  start: () => {},
  isTourActive: false,
});

export const TourProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [steps, setSteps] = useState<TourStep[]>();
  const [isTourActive, setIsTourActive] = useState(driver.isActive());
  const [onComplete, setOnComplete] = useState<() => void>();
  const intervals = useRef<number[]>([]);
  const listeners = useRef<{ event: string; element: ListernerElement; listener: Listener }[]>([]);

  const initDriver = useCallback(({ steps, onComplete }: TourOptions) => {
    setSteps(steps);
    setOnComplete(() => onComplete);
  }, []);

  const start = useCallback(() => {
    if (driver.isActive()) {
      driver.destroy();
    }

    const newSteps = (steps ?? []).map((s) => {
      return {
        ...s,
        element: getStepElementSelector(s),
        disableActiveInteraction: !s.interaction,
      } satisfies DriveStep;
    });

    const firstStepElement = getStepElement(newSteps[0]);
    if (!firstStepElement) {
      return;
    }

    driver.rootSteps = newSteps;
    driver.isDrivingThroughChildren = false;
    driver.lastRootIndex = 0;

    driver.setSteps(newSteps);
    driver.onComplete = onComplete;

    driver.drive();
    setIsTourActive(true);
  }, [steps, onComplete]);

  useEffect(() => {
    driver.setIsActive = setIsTourActive;

    driver.addInterval = (intervalId: number) => {
      if (!driver || !intervals.current) return;
      intervals.current = [...intervals.current, intervalId];
    };

    driver.clearIntervals = () => {
      if (!driver || !intervals.current) return;
      intervals.current.forEach(clearInterval);
      intervals.current = [];
    };

    driver.addListener = (event, element, listener) => {
      if (!driver) return;

      const currentListener = listeners.current.find((l) => l.event === event && l.element === element);

      if (currentListener) {
        currentListener.element.removeEventListener(currentListener.event, currentListener.listener);
      }

      listeners.current = [...listeners.current, { event, element, listener }];
    };

    driver.removeListeners = () => {
      if (!driver || !listeners.current?.length) return;

      listeners.current.forEach(({ event, element, listener }) => {
        element.removeEventListener(event, listener);
      });
      listeners.current = [];
    };

    return () => driver.destroy();
  }, []);

  const contextValue = useMemo(() => ({ initDriver, start, isTourActive }), [initDriver, start, isTourActive]);

  return (
    <TourContext.Provider value={contextValue}>
      {children}
      <PopoverPortal />
    </TourContext.Provider>
  );
};

export const useTour = (tourOptions: TourOptions) => {
  const { initDriver, start, isTourActive } = useContext(TourContext);

  useEffect(() => {
    initDriver(tourOptions);
  }, [tourOptions, initDriver]);

  return { isTourActive, start };
};

export const useTourContext = () => useContext(TourContext);
